import { PIN_LENGTH } from '@veriverse/shared';
import { create } from 'zustand';

/** What the server last said about the PIN that was keyed in. */
export type PinFeedback =
  | { readonly kind: 'wrong'; readonly attemptsLeft: number }
  | { readonly kind: 'locked'; readonly retryAt: number };

/**
 * Which PIN door the keypad is open for (ADR-0039); the keypad is shown while this is set. Also
 * remembers the doors whose code this player keyed in, so E offers lock and unlock for them. The
 * server enforces it; after a reload the player keys the code in again.
 */
export const usePinDoorStore = create<{
  objectId: number | undefined;
  feedback: PinFeedback | undefined;
  known: ReadonlySet<number>;
  openAt(objectId: number): void;
  setFeedback(feedback: PinFeedback | undefined): void;
  /** The server accepted the code of this door: close the keypad and remember it. */
  authorize(objectId: number): void;
  close(): void;
}>((set) => ({
  objectId: undefined,
  feedback: undefined,
  known: new Set(),
  openAt: (objectId) => set({ objectId, feedback: undefined }),
  setFeedback: (feedback) => set({ feedback }),
  authorize: (objectId) =>
    set((state) => ({
      known: new Set(state.known).add(objectId),
      objectId: undefined,
      feedback: undefined,
    })),
  close: () => set({ objectId: undefined, feedback: undefined }),
}));

/** What pressing E does at a PIN door, from its synced state and whether the code is known. */
export type PinDoorPress = 'keypad' | 'unlock' | 'menu' | 'use';

export function pinDoorPress(unlocked: boolean, knowsCode: boolean): PinDoorPress {
  if (!unlocked) return knowsCode ? 'unlock' : 'keypad';
  return knowsCode ? 'menu' : 'use';
}

/** The digits typed so far after a key press: digits are added up to the PIN length, `Backspace` removes. */
export function pressPinKey(entered: string, key: string): string {
  if (key === 'Backspace') return entered.slice(0, -1);
  if (/^\d$/.test(key) && entered.length < PIN_LENGTH) return entered + key;
  return entered;
}
